import { useCallback, useEffect, useState } from "react";
import { staffChatApi } from "@/api";
import { formatBytes } from "@/lib/shares";
import type { ChatAttachment } from "@/types";

/** Attachments under a message bubble, both directions. Images fetch a
 *  presigned URL on render and show a ~200px thumbnail; PDFs show their first
 *  page (rendered server-side at upload; older PDFs are backfilled the first
 *  time they're shown — see ensurePdfPreview); anything else is a file card.
 *  URLs expire in 300 s, so every click fetches a fresh one — and an expired
 *  thumbnail (img error) refetches on click instead of erroring. */
export function MessageAttachments({ attachments }: { attachments: ChatAttachment[] }) {
  return (
    <div className="chat-msg__attachments">
      {attachments.map((a) =>
        a.mime_type.startsWith("image/") ? (
          <ImageAttachment key={a.attachment_id} attachment={a} />
        ) : a.mime_type.toLowerCase() === "application/pdf" ? (
          <PdfAttachment key={a.attachment_id} attachment={a} />
        ) : (
          <FileAttachment key={a.attachment_id} attachment={a} />
        ),
      )}
    </div>
  );
}

/** Open a fresh presigned URL in a new tab. The tab opens synchronously (so
 *  popup blockers allow it) and is pointed at the URL once fetched. */
async function openInNewTab(attachmentId: number): Promise<boolean> {
  const tab = window.open("", "_blank");
  try {
    const { url } = await staffChatApi.getAttachmentDownloadUrl(attachmentId);
    if (tab) tab.location.replace(url);
    else window.open(url, "_blank", "noopener");
    return true;
  } catch {
    tab?.close();
    return false;
  }
}

function useOpenAttachment(attachmentId: number) {
  const [opening, setOpening] = useState(false);
  const [failed, setFailed] = useState(false);

  async function open() {
    if (opening) return;
    setOpening(true);
    setFailed(false);
    const ok = await openInNewTab(attachmentId);
    if (!ok) setFailed(true);
    setOpening(false);
  }

  return { opening, failed, open };
}

/* ── PDF previews (Xano fn chat/pdf_preview; backfill via api 379) ───────── */

// PDFs uploaded since 2026-10-01 arrive with has_thumb already set — Xano
// renders the preview during the upload. Older ones (and any whose render
// failed) are backfilled here: one server request at a time for the whole
// session, each attachment tried once per page load. A finished preview is
// permanent, and the phone apps show it too on their next poll.
const previewJobs = new Map<number, Promise<boolean>>();
let previewQueue: Promise<unknown> = Promise.resolve();

function ensurePdfPreview(attachmentId: number): Promise<boolean> {
  let job = previewJobs.get(attachmentId);
  if (!job) {
    job = previewQueue
      .then(() => staffChatApi.requestAttachmentPreview(attachmentId))
      .then(
        (r) => r.has_thumb,
        () => false,
      );
    previewQueue = job;
    previewJobs.set(attachmentId, job);
  }
  return job;
}

function PdfAttachment({ attachment }: { attachment: ChatAttachment }) {
  const id = attachment.attachment_id;
  const [made, setMade] = useState(false);
  const hasPreview = attachment.has_thumb === true || made;
  const [src, setSrc] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const { opening, failed, open } = useOpenAttachment(id);

  useEffect(() => {
    if (attachment.has_thumb) return;
    let live = true;
    void ensurePdfPreview(id).then((ok) => {
      if (live && ok) setMade(true);
    });
    return () => {
      live = false;
    };
  }, [id, attachment.has_thumb]);

  useEffect(() => {
    if (!hasPreview) return;
    let live = true;
    setSrc(null);
    setPreviewFailed(false);
    staffChatApi.getAttachmentPreviewUrl(id).then(
      ({ url }) => {
        if (live) setSrc(url);
      },
      () => {
        if (live) setPreviewFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [id, hasPreview]);

  if (!hasPreview || previewFailed) return <FileAttachment attachment={attachment} />;

  return (
    <button
      type="button"
      className="chat-attach-pdf"
      onClick={() => void open()}
      disabled={opening}
      title={`${attachment.file_name} — open PDF`}
    >
      <span className={`chat-attach-pdf__page${src ? "" : " chat-attach-pdf__page--loading"}`}>
        {src ? (
          <img src={src} alt="" onError={() => setPreviewFailed(true)} />
        ) : (
          <span className="chat-mini-spinner" aria-hidden="true" />
        )}
      </span>
      <span className="chat-attach-pdf__facts">
        <span className="chat-attach-file__name">{attachment.file_name}</span>
        <span className="chat-attach-file__size">
          {failed ? "Couldn't open — click to retry" : `PDF · ${formatBytes(attachment.file_size)}`}
        </span>
      </span>
    </button>
  );
}

function ImageAttachment({ attachment }: { attachment: ChatAttachment }) {
  const [src, setSrc] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);

  const load = useCallback(async () => {
    setSrc(null);
    setBroken(false);
    try {
      const { url } = await staffChatApi.getAttachmentDownloadUrl(attachment.attachment_id);
      setSrc(url);
    } catch {
      setBroken(true);
    }
  }, [attachment.attachment_id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (broken) {
    return (
      <button type="button" className="chat-attach-img chat-attach-img--reload" onClick={() => void load()}>
        <span className="chat-attach-file__name">{attachment.file_name}</span>
        <span className="chat-attach-file__size">Couldn't load — click to retry</span>
      </button>
    );
  }
  if (!src) {
    return (
      <span className="chat-attach-img chat-attach-img--loading" aria-label={`Loading ${attachment.file_name}`}>
        <span className="chat-mini-spinner" aria-hidden="true" />
      </span>
    );
  }
  return (
    <button
      type="button"
      className="chat-attach-img"
      onClick={() => void openInNewTab(attachment.attachment_id)}
      title={`${attachment.file_name} — open full size`}
    >
      <img src={src} alt={attachment.file_name} onError={() => setBroken(true)} />
    </button>
  );
}

function FileAttachment({ attachment }: { attachment: ChatAttachment }) {
  const { opening, failed, open } = useOpenAttachment(attachment.attachment_id);

  return (
    <button type="button" className="chat-attach-file" onClick={() => void open()} disabled={opening}>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
      </svg>
      <span className="chat-attach-file__facts">
        <span className="chat-attach-file__name">{attachment.file_name}</span>
        <span className="chat-attach-file__size">
          {failed ? "Couldn't open — click to retry" : formatBytes(attachment.file_size)}
        </span>
      </span>
    </button>
  );
}
